import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";

import { graphql } from "../../gql";
import {
  addBookingResultNotification,
  type BookingResult,
} from "../../services/notifications/notificationsActions";
import { showBookingResultToast } from "../../services/notifications/utils/showToast";
import { initializeUpdateFormWithBooking } from "../../services/update-form/updateFormActionsHelpers";
import { formIdVariants } from "../../services/update-form/updateFormReducer";

export const cancelBookingMutation = graphql(/*GraphQL*/ `
  mutation CancelBookingMutation($bookingNo: Int!, $timestamp: String!) {
    cancelBooking(bookingNo: $bookingNo, timestamp: $timestamp) {
      booking {
        ...BasicBooking
      }
      bookingOutOfDate
      errors {
        description
      }
      messages {
        description
      }
      warnings {
        description
      }
      success
    }
  }
`);

interface BookingWithBookingNoAndTimestamp {
  bookingNo: number;
  timestamp: string;
}
interface UseCancelBookingParams {
  booking: BookingWithBookingNoAndTimestamp;
  onFail?: () => void;
  onSuccess?: () => void;
}

export const useCancelBooking = ({
  booking: { bookingNo, timestamp },
  onFail,
  onSuccess,
}: UseCancelBookingParams) => {
  const dispatch = useDispatch();

  const [submitting, setSubmitting] = useState(false);

  const [cancelBooking] = useMutation(cancelBookingMutation, {
    refetchQueries: ["DashboardQuery", "PaymentDetailsQuery"],
    variables: { bookingNo, timestamp },
  });

  const submit = useCallback(() => {
    setSubmitting(true);
    cancelBooking()
      .then((result) => {
        setSubmitting(false);

        if (result.data?.cancelBooking) {
          if (result.data.cancelBooking.success) {
            const cancelledBooking = result.data.cancelBooking.booking!;

            const bookingResult: BookingResult = {
              type: "cancel-success",
              bookingNo: cancelledBooking.bookingNo,
              messages: result.data.cancelBooking.messages.map(
                (msg) => msg.description,
              ),
              warnings: result.data.cancelBooking.warnings.map(
                (msg) => msg.description,
              ),
            };

            formIdVariants.forEach((variant) => {
              dispatch(
                initializeUpdateFormWithBooking(cancelledBooking, variant),
              );
            });

            showBookingResultToast(bookingResult);
            dispatch(addBookingResultNotification(bookingResult));

            onSuccess?.();
          } else {
            const bookingResult: BookingResult = {
              bookingNo,
              type: "cancel-fail",
              errors: result.data.cancelBooking.errors.map(
                (msg) => msg.description,
              ),
            };

            showBookingResultToast(bookingResult);
            dispatch(addBookingResultNotification(bookingResult));

            onFail?.();
          }
        }
      })
      .catch(() => {
        setSubmitting(false);

        onFail?.();
      });
  }, [bookingNo, cancelBooking, dispatch, onFail, onSuccess]);

  return {
    submit,
    submitting,
  };
};
